import { useContext, useEffect, useMemo, useRef } from 'react';
import { uuidSingleton } from 'components/filter/filters-next/defaults';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { useFlattenedTableColumnFilters } from 'components/filter/filters-next/useFlattenedTableColumnFilters';
import { ConditionalOperator } from 'components/filter/types';
import { TableContext } from 'components/table/TableContext';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { first, intersectionBy, isEqual } from 'lodash';
import { FilterCacheKey } from 'types/filter-cache-key';
import { GivebutterColumnFilterState } from 'types/react-table';

const useBidirectionalTableAndFilterCacheSync = (cacheKey: FilterCacheKey) => {
    useSyncFilters(cacheKey);
    useSyncTableColumnFiltersToFilterCache(cacheKey);
};

const useSyncFilters = (cacheKey: FilterCacheKey) => {
    const tableColumns = useFlattenedTableColumnFilters();
    const filters = useMemo(
        () => [
            {
                id: uuidSingleton,
                innerOperator: 'and' as ConditionalOperator,
                outerOperator: 'and' as ConditionalOperator,
                filters: tableColumns,
            },
        ],
        [tableColumns],
    );

    const cacheKeyRef = useRef(cacheKey);
    const updateFilterRaw = useFiltersCache(state => state.updateFiltersNextByKey);
    const updateFilter = useRef(updateFilterRaw);

    useEffect(() => {
        if (filters) {
            updateFilter.current(cacheKeyRef.current, filters);
        }
    }, [filters]);
};

const useSyncTableColumnFiltersToFilterCache = (cacheKey: FilterCacheKey) => {
    const { table } = useContext(TableContext);
    const { didInit, currentGroup } = useFiltersByKey(cacheKey);
    const lastCurrentGroup = useRef(currentGroup);
    const filterState = table.getState().givebutterColumnFilters;
    const setFilterState = table.setGivebutterColumnFilters;

    useEffect(() => {
        if (
            didInit &&
            currentGroup &&
            lastCurrentGroup.current &&
            !isEqual(currentGroup, lastCurrentGroup.current)
        ) {
            const nextColumnState = Object.entries(filterState).reduce((prev, [key, curr]) => {
                const columnFilters = first(curr);
                if (columnFilters) {
                    const nextValues = intersectionBy(
                        currentGroup.filters,
                        columnFilters.filters,
                        f => f.id,
                    );

                    prev[key] = [
                        {
                            id: uuidSingleton,
                            innerOperator: 'and',
                            outerOperator: 'and',
                            filters: nextValues,
                        },
                    ];
                }

                return prev;
            }, {} as GivebutterColumnFilterState);

            lastCurrentGroup.current = currentGroup;
            setFilterState(nextColumnState);
        }
    }, [currentGroup, didInit, filterState, setFilterState]);
};

export { useBidirectionalTableAndFilterCacheSync };
